// Bảng route của khu Quản trị NPP (docs/KE_HOACH_NPP_SANG_WEB.md §5). Màn chuyển từ trang admin (npp/legacy)
// tải lười — người dùng đơn vị không tải phần quản trị. Quyền mỗi màn khớp trang admin cũ (ROUTE_PERMISSIONS).

import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, useLocation, useParams } from 'react-router-dom'
import { NppRoute } from './NppGate'
import { NPP_ROUTE_PERMISSIONS as P } from './nav'
import { Spinner } from '@/components/ui/spinner'
import NppOverviewPage from './routes/NppOverview'
import NppAccountPage from './routes/NppAccount'

const NppNetwork = lazy(() => import('./routes/NppNetwork'))
const NppAnnouncements = lazy(() => import('./routes/NppAnnouncements'))

const Shops = lazy(() => import('./legacy/routes/Shops'))
const ShopDetail = lazy(() => import('./legacy/routes/ShopDetail'))
const BulkCreateUnits = lazy(() => import('./legacy/routes/BulkCreateUnits'))
const Upgrades = lazy(() => import('./legacy/routes/Upgrades'))
const AccountDeletions = lazy(() => import('./legacy/routes/AccountDeletions'))
const Orders = lazy(() => import('./legacy/routes/Orders'))
const OrderApprovals = lazy(() => import('./legacy/routes/OrderApprovals'))
const Tickets = lazy(() => import('./legacy/routes/Tickets'))
const PriceLists = lazy(() => import('./legacy/routes/PriceLists'))
const Commissions = lazy(() => import('./legacy/routes/Commissions'))
const CarrierStatements = lazy(() => import('./legacy/routes/CarrierStatements'))
const CarrierStatementDetail = lazy(() => import('./legacy/routes/CarrierStatementDetail'))
const Payouts = lazy(() => import('./legacy/routes/Payouts'))
const Debts = lazy(() => import('./legacy/routes/Debts'))
const Finance = lazy(() => import('./legacy/routes/Finance'))
const SystemSettings = lazy(() => import('./legacy/routes/SystemSettings'))
const AdminUsers = lazy(() => import('./legacy/routes/AdminUsers'))
const Sagas = lazy(() => import('./legacy/routes/Sagas'))
const Audit = lazy(() => import('./legacy/routes/Audit'))

/** Trang Tài khoản cũ đã gộp vào Người dùng (/npp/units) — giữ từ khoá tìm (tên, SĐT, email chủ). */
function UsersToPeople() {
  const q = new URLSearchParams(useLocation().search).get('q')
  return <Navigate to={`/npp/units${q ? `?q=${encodeURIComponent(q)}` : ''}`} replace />
}

function Loading() {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted">
      <Spinner /> Đang tải…
    </div>
  )
}

function page(anyOf: readonly string[], el: ReactNode) {
  return (
    <NppRoute anyOf={anyOf}>
      <Suspense fallback={<Loading />}>{el}</Suspense>
    </NppRoute>
  )
}

/** /npp/payouts/runs/:id → tab "Đợt chi" của trang Chi trả (như trang admin cũ). */
function PayoutRunRedirect() {
  const { id = '' } = useParams()
  return <Navigate to={`/npp/payouts?tab=runs&run=${encodeURIComponent(id)}`} replace />
}

const UNIT_MANAGE = ['units.agency.manage', 'units.ctv.manage', 'units.shop.manage'] as const

export const nppRoutes = (
  <>
    <Route path="/npp" element={page(P.dashboard, <NppOverviewPage />)} />
    <Route path="/npp/account" element={page([], <NppAccountPage />)} />

    <Route path="/npp/network" element={page(P.units, <NppNetwork />)} />
    <Route path="/npp/units" element={page(P.units, <Shops />)} />
    <Route path="/npp/units/bulk" element={page(UNIT_MANAGE, <BulkCreateUnits />)} />
    <Route path="/npp/units/:id" element={page(P.units, <ShopDetail />)} />
    <Route path="/npp/upgrades" element={page(P.upgrades, <Upgrades />)} />
    <Route path="/npp/announcements" element={page(P.announcements, <NppAnnouncements />)} />
    <Route path="/npp/users" element={<UsersToPeople />} />
    <Route path="/npp/account-deletions" element={page(P.accountDeletions, <AccountDeletions />)} />

    <Route path="/npp/orders" element={page(P.orders, <Orders />)} />
    <Route path="/npp/order-approvals" element={page(P.orderApprovals, <OrderApprovals />)} />
    <Route path="/npp/tickets" element={page(P.tickets, <Tickets />)} />

    <Route path="/npp/price-lists" element={page(P.priceLists, <PriceLists />)} />
    <Route path="/npp/rate-cards" element={<Navigate to="/npp/price-lists" replace />} />
    <Route path="/npp/commissions" element={page(P.commissions, <Commissions />)} />

    <Route path="/npp/carrier-statements" element={page(P.carrierStatements, <CarrierStatements />)} />
    <Route path="/npp/carrier-statements/:id" element={page(P.carrierStatements, <CarrierStatementDetail />)} />
    <Route path="/npp/payouts" element={page(P.payouts, <Payouts />)} />
    <Route path="/npp/payouts/runs/:id" element={<PayoutRunRedirect />} />
    <Route path="/npp/debts" element={page(P.debts, <Debts />)} />
    <Route path="/npp/finance" element={page(P.finance, <Finance />)} />

    <Route path="/npp/settings" element={page(P.settingsPage, <SystemSettings />)} />
    <Route path="/npp/master-settings" element={<Navigate to="/npp/settings?tab=best" replace />} />
    <Route path="/npp/admins" element={page(P.admins, <AdminUsers />)} />
    <Route path="/npp/sagas" element={page(P.sagas, <Sagas />)} />
    <Route path="/npp/audit" element={page(P.audit, <Audit />)} />

    <Route path="/npp/*" element={<Navigate to="/npp" replace />} />
  </>
)
